/**
 * Module dependencies.
 */

import { Button, Loading, Subtitle, Type } from 'src/components/core';
import { ModelRangeProps, ModelRangeSpec } from 'src/types/model-ranges';
import { ReactElement, forwardRef, useEffect, useMemo, useState } from 'react';
import { theme as amtrolAlfaTheme } from 'src/styles/theme';
import { media, units, useBreakpoint } from '@untile/react-components';
import { prop, theme } from 'styled-tools';
import { useFiltersList } from 'src/context/filters-list/context';
import { useInView } from 'react-intersection-observer';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useSectionsNavigation } from 'src/context/sections-navigation/context';
import { useSort } from 'src/context/sort/context';
import Container from 'src/components/core/layout/container';
import Filters from 'src/components/model-ranges/filters';
import ModelRangeCard from 'src/components/cards/model-range-card';
import ModelRangeSpecsCard from 'src/components/cards/model-range-specs-card';
import chunk from 'lodash/chunk';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import styled from 'styled-components';
import useModelRanges from 'src/api/model-ranges/use-model-ranges';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  hasButanePropaneSpecs?: boolean;
  modelRangeFilters: any;
  modelRanges: {
    pageParams: number[];
    pages: Array<{
      data: ModelRangeProps[];
      nextPage: number | null;
      total: number;
    }>;
  };
  technologyId: string;
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  padding: ${units(6)} 0 ${units(11)};
  position: relative;

  ${media.min('md')`
    padding: ${units(18)} 0;
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '. title   .'
    '. total   .'
    '. filters .'
    '. list    list';
  grid-template-columns:
    ${theme('grid.gutterMobile')}px calc(
      100% - 2 * ${theme('grid.gutterMobile')}px
    )
    ${theme('grid.gutterMobile')}px;

  ${media.min('md')`
    grid-template-areas:
      '. title   .'
      '. total   .'
      '. filters .'
      '. list    .';
    grid-template-columns: calc(1 / 12 * 100%) calc(10 / 12 * 100%) calc(1 / 12 * 100%);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  grid-area: title;

  ${media.min('md')`
    text-align: center;
  `}
`;

/**
 * `TotalProducts` styled component.
 */

const TotalProducts = styled(Subtitle)`
  grid-area: total;

  ${media.min('md')`
    text-align: center;
  `}
`;

/**
 * `FiltersWrapper` styled component.
 */

const FiltersWrapper = styled.div`
  grid-area: filters;
  margin-bottom: ${units(4)};
  padding-top: ${units(3)};

  ${media.min('md')`
    justify-self: start;
    margin-bottom: ${units(3)};
    padding-top: 0;
  `}
`;

/**
 * `StyledLoading` styled component.
 */

const StyledLoading = styled(Loading)`
  top: 100px;
  transform: translateX(-50%);
`;

/**
 * `Empty` styled component.
 */

const Empty = styled.div`
  ${theme('typography.styles.h6')}

  font-weight: 400;
  padding-top: ${units(3)};
  text-align: center;
`;

/**
 * `ListWrapper` styled component.
 */

const ListWrapper = styled.div`
  grid-area: list;
  min-height: 250px;
  position: relative;

  ${media.max('md')`
    overflow-x: auto;
  `}
`;

/**
 * `List` styled component.
 */

const List = styled.div`
  padding-bottom: ${units(5)};
`;

/**
 * `ListRow` styled component.
 */

const ListRow = styled.div<{ divider: number }>`
  display: grid;
  grid-template-columns: repeat(${prop('divider')}, max-content);
`;

/**
 * `FirstListItemWrapper` styled component.
 */

const FirstListItemWrapper = styled.div`
  display: flex;
`;

/**
 * `LoadMore` styled component.
 */

const LoadMore = styled.div`
  padding: ${units(6)} 0;
  text-align: center;
`;

/**
 * `ModelRangesList` component.
 */

const ModelRangesList = (
  props: Props,
  sectionRef: any
): ReactElement | null => {
  const {
    className,
    hasButanePropaneSpecs = true,
    modelRangeFilters,
    modelRanges,
    technologyId
  } = props;

  const { translate } = useTranslate();
  const { submittedFilters } = useFiltersList();
  const { submittedSort } = useSort();
  const isMobile = useBreakpoint('ms', 'max');
  const isTablet = useBreakpoint('md', 'max');
  const routeResolver = useLocalizedRoute();
  const [divider, setDivider] = useState<number>(5);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isSuccess,
    refetch
  } = useModelRanges({
    filterParams: submittedFilters,
    initialData: modelRanges,
    sortParam: submittedSort,
    technologyId
  });

  const chunkedData: ModelRangeProps[][] = useMemo(
    () => chunk(flatMap(data?.pages, 'data'), divider),
    [data?.pages, divider]
  );

  const total = data?.pages[0]?.total ?? 0;
  const isUpdating = isFetchingNextPage || isFetching;
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  const [ref, inView] = useInView({ threshold: 0.5 });
  const { onSetCurrentSection } = useSectionsNavigation();
  const [hoveredSpec, setHoveredSpec] = useState<ModelRangeSpec | null>(null);
  const cardWidth = isMobile ? 136 : 208;
  const specsCardWidth = isMobile ? 127 : 238;

  useEffect(() => {
    if (inView) {
      onSetCurrentSection('modelRanges');
    }
  }, [inView, onSetCurrentSection]);

  useEffect(() => {
    function handleWindowResize() {
      const listWidth = isTablet
        ? window.innerWidth - 2 * amtrolAlfaTheme.grid.gutterMobile
        : (window.innerWidth * 10) / 12;

      const totalCards = (listWidth - specsCardWidth) / cardWidth;

      setDivider(Math.floor(totalCards + 0.5));
    }

    handleWindowResize();

    window.addEventListener('resize', handleWindowResize);

    return () => window.removeEventListener('resize', handleWindowResize);
  }, [cardWidth, isTablet, specsCardWidth]);

  if (isEmpty(modelRanges.pages[0].data)) {
    return null;
  }

  return (
    <Section
      className={className}
      ref={sectionRef}
    >
      <StyledContainer ref={ref}>
        <Grid>
          <Title {...animProps}>
            {translate('technologies:modelRanges.title')}
          </Title>

          {total > 0 && (
            <TotalProducts {...animProps}>
              {`${total} ${translate('technologies:product', {
                count: total
              })}`}
            </TotalProducts>
          )}

          <FiltersWrapper>
            <Filters
              isUpdating={isUpdating}
              isUpdatingSucceed={isSuccess}
              modelRangeFilters={modelRangeFilters}
              onUpdate={refetch}
            />
          </FiltersWrapper>

          {isSuccess && (
            <ListWrapper>
              <StyledLoading active={isUpdating} />

              {!isLoading && !isUpdating && total === 0 && (
                <Empty {...animProps}>
                  {translate('technologies:modelRanges.emptyList')}
                </Empty>
              )}

              <div
                data-scroll={'fade-in-up-small'}
                data-scroll-offset={'5%'}
              >
                {!isLoading && !isFetching && total > 0 && (
                  <>
                    <List>
                      {map(chunkedData, (row, rowIndex) => (
                        <ListRow
                          divider={divider}
                          key={`${rowIndex}-${JSON.stringify(
                            submittedFilters
                          )}-${JSON.stringify(submittedSort)}`}
                        >
                          {map(row, (modelRange, modelRangeIndex) => (
                            <FirstListItemWrapper key={modelRange.id}>
                              {modelRangeIndex === 0 && (
                                <ModelRangeSpecsCard
                                  hasButanePropaneSpecs={hasButanePropaneSpecs}
                                  hoveredSpec={hoveredSpec}
                                  onHoverSpec={setHoveredSpec}
                                />
                              )}

                              <ModelRangeCard
                                hasButanePropaneSpecs={hasButanePropaneSpecs}
                                hoveredSpec={hoveredSpec}
                                isLastCard={modelRangeIndex === divider - 1}
                                onHoverSpec={setHoveredSpec}
                                url={routeResolver('modelRangeDetails', {
                                  slug: modelRange.slug
                                })}
                                {...modelRange}
                              />
                            </FirstListItemWrapper>
                          ))}
                        </ListRow>
                      ))}
                    </List>

                    {hasNextPage && (
                      <LoadMore {...animProps}>
                        <Button
                          colorTheme={'secondary'}
                          disabled={isUpdating}
                          isLoading={isUpdating}
                          onClick={() => fetchNextPage()}
                        >
                          {translate('common:actions.loadMoreProducts')}
                        </Button>
                      </LoadMore>
                    )}
                  </>
                )}
              </div>
            </ListWrapper>
          )}
        </Grid>
      </StyledContainer>
    </Section>
  );
};

/**
 * Export `ModelRangesList` component.
 */

export default forwardRef(ModelRangesList);
